import { useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';

const mapLocationToOption = (location) => ({
    value: location.id,
    label: location.locationName,
    locationAddress: location.locationAddress,
    organizationId: location.organizationId,
    parentLocationId: location.parentLocationId,
    childLocation: location.childLocation,
    raw: location,
});

const ParentLocationSelector = ({
    value,
    onChange,
    excludeIds = [],
    selectStyles,
    hasError = false,
    placeholder,
    isDisabled = false,
}) => {
    const { t } = useTranslation();

    const fetchLocations = useCallback(async (search = '') => {
        try {
            const params = { count: 10 };
            if (search) params.search = search;
            const { data } = await api.get('organization-location/get-locations-info', { params });
            return (data?.response || [])
                .filter((l) => !excludeIds.includes(l.id))
                .map(mapLocationToOption);
        } catch (err) {
            console.error('Error fetching parent locations:', err);
            return [];
        }
    }, [excludeIds]);

    const selectedOption = value ? mapLocationToOption(value) : null;

    const formatOptionLabel = (option) => (
        <div className="d-flex align-items-center justify-content-between">
            <div>
                <div className="fw-medium">{option.label}</div>
                {option.locationAddress && (
                    <small className="text-muted">{option.locationAddress}</small>
                )}
            </div>
            {option.childLocation !== undefined && (
                <span
                    className={`badge ms-2 ${option.childLocation ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}
                >
                    {option.childLocation
                        ? t('business.locations.child_location', 'Child Location')
                        : t('business.locations.parent_location_badge', 'Parent Location')}
                </span>
            )}
        </div>
    );

    return (
        <AsyncSelect
            defaultOptions
            loadOptions={fetchLocations}
            value={selectedOption}
            onChange={(opt) => onChange(opt ? opt.raw : null)}
            formatOptionLabel={formatOptionLabel}
            placeholder={placeholder || t('business.locations.search_parent_location', 'Search for parent location...')}
            isClearable
            isSearchable
            isDisabled={isDisabled}
            classNamePrefix="react-select"
            styles={selectStyles ? selectStyles(hasError) : undefined}
            noOptionsMessage={() => t('business.locations.no_locations', 'No locations found')}
            loadingMessage={() => t('shared.loading', 'Loading...')}
        />
    );
};

export default ParentLocationSelector;
